import { HelperParams } from './helper-params';
import { NavigationExtras, Params, PRIMARY_OUTLET, UrlTree } from '@angular/router';

interface UrlBuilderParams {
    preserveFragments?: boolean;
    preserveQueryParams?: boolean;
    urlSegmentGroupKey?: string;
    queryParams?: Params;
    saveReturnUrl?: boolean;
    navigationExtras?: NavigationExtras;
}

export class BaseBuildPart {
    protected readonly returnUrlQueryKey = 'returnUrl';
    protected readonly outletName = 'global-popup';

    constructor(private readonly baseHelperParams: HelperParams,
                private readonly parent: BaseBuildPart,
                private readonly commands: any[]) {
        this.parent = parent;

        this.commands = parent != null ?
            [...this.parent.commands, ...commands] :
            [...commands];
    }

    public url(params?: UrlBuilderParams): any[] {
        const commands = this.prepareCommands(params);
        return ['/', ...commands];
    }

    public urlTree(params?: UrlBuilderParams): UrlTree {
        const router = this.baseHelperParams.router;
        const commands = this.prepareCommands(params);

        let queryParams: Params = [];

        if (params) {
            if (params.preserveQueryParams) {
                queryParams = router.parseUrl(router.url).queryParams;
            } else if (params.queryParams) {
                queryParams = params.queryParams;
            }
        }

        return router.createUrlTree(commands, {queryParams: queryParams});
    }

    public navigate(params?: UrlBuilderParams): Promise<boolean> {
        const urlTree = this.getNavigationUrlTree(params);
        const navigationExtras = (params && params.navigationExtras) || undefined;

        return this.baseHelperParams.router.navigateByUrl(urlTree, navigationExtras);
    }

    public getUrl(params?: UrlBuilderParams): string {
        const urlTree = this.getNavigationUrlTree(params);

        return urlTree.toString();
    }

    public reload(params?: UrlBuilderParams) {
        const urlTree = this.getNavigationUrlTree(params);
        const url = this.baseHelperParams.urlSerializer.serialize(urlTree);
        window.location.href = this.baseHelperParams.location.prepareExternalUrl(url);
    }

    public getExistingReturnUrl(): string | null {
        if (!window.location.search) {
            return null;
        }

        const queryParams = window.location.search.split('&');

        for (let queryParam of queryParams) {
            const result = new RegExp(`${this.returnUrlQueryKey}=(.*)`).exec(queryParam);

            if (result && result[1]) {
                return result[1];
            }
        }

        return null;
    }

    private getNavigationUrlTree(params?: UrlBuilderParams) {
        if (params && params.saveReturnUrl) {
            if (!params.queryParams) {
                params.queryParams = {};
            }

            const returnUrl = this.getExistingReturnUrl();

            params.queryParams[this.returnUrlQueryKey] = returnUrl ? decodeURIComponent(returnUrl) : this.getReturnUrl();
        }

        return this.urlTree(params);
    }

    private getReturnUrl(): string {
        const baseElement = window.document.querySelector('base');

        if (!baseElement) {
            return location.href;
        }

        const returnUrl = location.href.substring(baseElement.href.length);

        return returnUrl ? returnUrl : '/';
    }

    private prepareCommands(params?: UrlBuilderParams) {
        const router = this.baseHelperParams.router;
        const commands = [...this.commands];

        if (params && params.preserveFragments) {
            const urlSegmentGroupKey = params.urlSegmentGroupKey || PRIMARY_OUTLET;
            const currentPaths = this.baseHelperParams.router.parseUrl(router.url).root.children[urlSegmentGroupKey].segments.map(s => s.path);

            const subCommands = currentPaths.length > this.commands.length ?
                currentPaths.slice(this.commands.length) :
                [];

            commands.push(...subCommands);
        }

        return commands.length > 0 ? commands : ['/'];
    }
}